import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Resolve,
  Router,
  RouterStateSnapshot
} from '@angular/router';
import { IAccountData } from '@app/models/account-data.model';
import { AppNames } from '@app/models/AppNames';
import { IFiProducts } from '@app/models/fi-products.model';
import { ILoan } from '@app/models/loan.model';
import { LoanStatus } from '@app/models/Loan-Status';
import { ILosInterface } from '@app/models/los-interface.model';
import { Pages } from '@app/models/Pages';
import { IProposal } from '@app/models/proposal.model';
import { IStatus } from '@app/models/status.model';
import { IUserAccMarketPlace } from '@app/models/user-info.model';
import { loanStatusText } from '@app/shared/constants/loanStatusText';
import * as _ from 'lodash';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';

import { AccountService } from './account.service';
import { FiProductsService } from './fi-products.service';
import { LoanInfoService } from './loan-info.service';
import { LoggingService } from './logging.service';
import { LosInterfaceService } from './los-interface.service';
import { ProposalService } from './proposal.service';
import { UserInfoService } from './user-info.service';

type proposalResolver = {
  products: IFiProducts;
  proposal: IProposal;
  accountInfo: IAccountData;
  statusInfo: IStatus;
};
type FAs = [IStatus, IAccountData, IUserAccMarketPlace];

@Injectable({
  providedIn: 'root'
})
export class ProposalResolver implements Resolve<proposalResolver> {
  eLoanStatus = LoanStatus;

  constructor(
    private header: LoanInfoService,
    private proposalService: ProposalService,
    private losInterfaceService: LosInterfaceService,
    private accountService: AccountService,
    private userInfoService: UserInfoService,
    private router: Router,
    private fiproducts: FiProductsService,
    private ls: LoggingService
  ) {}

  resolve(
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Observable<proposalResolver> {
    const isAppNameExpress: boolean =
      sessionStorage.getItem('appName') === AppNames.UNITY_EXPRESS;
    const proposalId: string =
      this.router.getCurrentNavigation().extras.state?.id;
    const status: LoanStatus =
      this.router.getCurrentNavigation().extras.state?.status;
    // console.log("Prop resolver line 62", status)
    return this.userInfoService.getUserInfo().pipe(
      concatMap((userInfoData: IUserAccMarketPlace) => {
        // console.log("Prop resolver line 65", userInfoData)
        const getStatusCall = isAppNameExpress
          ? this.losInterfaceService
              .getLoanStatus(
                userInfoData[this.userInfoService.getAccountIndex(userInfoData)]
              )
              .pipe(catchError(() => of(<ILosInterface>{})))
          : of(<ILosInterface>{});
        const getCUNameCall = this.accountService.getAccount(
          userInfoData[this.userInfoService.getAccountIndex(userInfoData)]
        );
        const getUserInfo = of(userInfoData);
        return forkJoin([getStatusCall, getCUNameCall, getUserInfo]);
      }),
      concatMap(([statusInfo, ...remaining]) => {
        // console.log("Prop resolver line 73", statusInfo);
        if (!isAppNameExpress && proposalId && status === LoanStatus.Proposal) {
          // console.log("Prop resolver line 75");
          return <Observable<[FAs, ILoan | {}]>>(
            combineLatest(
              of([statusInfo, ...remaining]),
              this.proposalService.getProposal({ id: proposalId })
            )
          );
        } else if (
          statusInfo.loanStatus !== LoanStatus.Booking &&
          statusInfo.loanStatus !== LoanStatus.Void
        ) {
          // console.log("Prop resolver line 81", statusInfo);
          return <Observable<[FAs, ILoan | {}]>>(_.isEmpty(statusInfo)
            ? of([[statusInfo, ...remaining], null])
            : combineLatest([
                of([statusInfo, ...remaining]),
                this.proposalService.getProposal({
                  id: proposalId || statusInfo.quoteLoanId
                })
              ]));
        } else {
          // console.log("Prop resolver line 93", statusInfo);
          return <Observable<[FAs, ILoan | {}]>>(
            (_.isEmpty(statusInfo)
              ? of([[statusInfo, ...remaining], null])
              : combineLatest(of([statusInfo, ...remaining])))
          );
        }
      }),
      map(([[statusInfo, accountInfo, userInfo], proposal]) => {
        // To populate the data on header before page is loaded
        this.header.loanInfo.next({
          loName: `${userInfo[this.userInfoService.getAccountIndex(userInfo)].AccountName} `,
          loanStatus: _.isEmpty(statusInfo)
            ? loanStatusText.New
            : statusInfo.loanStatus,
          creditUnionName: accountInfo.fiName
        });
        return { userInfo, proposal, accountInfo, statusInfo };
      }),
      concatMap(({ userInfo, proposal, accountInfo, statusInfo }) => {
        return forkJoin({
          products: this.fiproducts.getFiProducts(
            userInfo[this.userInfoService.getAccountIndex(userInfo)]
          )
        }).pipe(
          map(({ products }) => ({
            products,
            proposal,
            accountInfo,
            statusInfo
          }))
        );
      }),
      catchError(() => {
        //Redirecting to error page since we couldn't load the page without resolver data
        this.ls.sendErrorLevelMessage('Proposal Resolver Error', this, {
          error: 'There was an error completing the proposal resolver.'
        });
        return of(null);
      })
    );
  }
}
